import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {StatusEnum} from "../../../../../commons/enums/status-enum";
import {manageFootballCompetitionService} from "../../../../../commons";
import {FootballTournamentManager} from "../../../../../commons/models/football/football-tournament-manager";
import {EnsueCollection} from "../../../../../ensue-react-system/utilities/ensue-collection";

export interface ITournamentManagerState {
    managers: EnsueCollection<FootballTournamentManager>;
    managerFetchStatus: StatusEnum;
    addManagerStatus: StatusEnum,
    removeManagerStatus: StatusEnum,
}

const initialState: ITournamentManagerState = {
    managers: new EnsueCollection<FootballTournamentManager>(),
    managerFetchStatus: StatusEnum.Idle,
    addManagerStatus: StatusEnum.Idle,
    removeManagerStatus: StatusEnum.Idle,
};

interface ITournamentManagerAddRemoveParams {
    tournamentId: number;
    userId: number;
    remove?: boolean
}

export const fetchTournamentManager = createAsyncThunk<EnsueCollection<FootballTournamentManager>, number, {
    rejectValue: unknown
}>(
    "tournamentManager/fetch",
    async (tournamentId: number, {rejectWithValue}) => {
        try {
            return await manageFootballCompetitionService.getTournamentManagers(tournamentId);
        } catch (e) {
            return rejectWithValue(e);
        }
    }
);

export const addRemoveTournamentManager = createAsyncThunk<FootballTournamentManager, ITournamentManagerAddRemoveParams, {
    rejectValue: unknown
}>(
    "tournamentManager/addRemove",
    async (params: ITournamentManagerAddRemoveParams, {rejectWithValue}) => {
        try {
            return await manageFootballCompetitionService.addRemoveTournamentManager(params.tournamentId, params.userId, params.remove);
        } catch (e) {
            return rejectWithValue(e);
        }
    }
);

const footballTournamentManagerSlice = createSlice({
    name: "tournamentManager",
    initialState: initialState,
    reducers: {
        resetManagerState(state) {
            state.managers = new EnsueCollection<FootballTournamentManager>();
            state.managerFetchStatus = StatusEnum.Idle;
            state.addManagerStatus = StatusEnum.Idle;
            state.removeManagerStatus = StatusEnum.Idle;
        },
        resetAddRemoveStatus(state) {
            state.addManagerStatus = StatusEnum.Idle;
            state.removeManagerStatus = StatusEnum.Idle;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchTournamentManager.pending, (state) => {
                state.managerFetchStatus = StatusEnum.Loading;
            })
            .addCase(fetchTournamentManager.fulfilled, (state, action: PayloadAction<EnsueCollection<FootballTournamentManager>>) => {
                state.managerFetchStatus = StatusEnum.Succeeded;
                state.managers = action.payload;
            })
            .addCase(fetchTournamentManager.rejected, (state) => {
                state.managerFetchStatus = StatusEnum.Failed;
            })

            // Add/Remove manager
            .addCase(addRemoveTournamentManager.pending, (state) => {
                state.addManagerStatus = StatusEnum.Loading;
            })
            .addCase(addRemoveTournamentManager.fulfilled, (state) => {
                state.addManagerStatus = StatusEnum.Succeeded;
            })
            .addCase(addRemoveTournamentManager.rejected, (state) => {
                state.addManagerStatus = StatusEnum.Failed;
            })
        ;
    },
});

export const {resetManagerState, resetAddRemoveStatus} = footballTournamentManagerSlice.actions;
export default footballTournamentManagerSlice.reducer;
